import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { CardapioEstabelecimentoDTO } from '../../../../models/cardapio-digital/cardapio-digital.model';
import { HorarioDia, parseHorariosFuncionamento } from '../../utils/cardapio-horario.util';

@Component({
  selector: 'app-cardapio-loja-fechada-dialog',
  templateUrl: './cardapio-loja-fechada-dialog.component.html',
  styleUrls: ['./cardapio-loja-fechada-dialog.component.scss'],
  providers: [MessageService],
})
export class CardapioLojaFechadaDialogComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Input() estabelecimento!: CardapioEstabelecimentoDTO;
  @Output() visibleChange = new EventEmitter<boolean>();

  horarios: HorarioDia[] = [];
  dataAtual = '';
  horaAtual = '';

  private langSub?: Subscription;
  private clockTimer?: ReturnType<typeof setInterval>;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.parseHorarios();
    this.atualizarDataHora();
    this.clockTimer = setInterval(() => this.atualizarDataHora(), 60_000);
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.parseHorarios();
      this.atualizarDataHora();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
    if (this.clockTimer) {
      clearInterval(this.clockTimer);
    }
  }

  onShow(): void {
    this.parseHorarios();
    this.atualizarDataHora();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  /** Retorna link do WhatsApp */
  getWhatsappLink(): string {
    return `https://wa.me/${this.estabelecimento?.telefoneWhatsapp}`;
  }

  /** Retorna link do Instagram */
  getInstagramLink(): string {
    return `https://instagram.com/${this.estabelecimento?.perfilInstagram}`;
  }

  /** Retorna link do Facebook */
  getFacebookLink(): string {
    return `https://facebook.com/${this.estabelecimento?.perfilFacebook}`;
  }

  /** Compartilhar link do cardapio via Web Share API ou fallback clipboard */
  compartilhar(): void {
    const url = window.location.href;
    const titulo = this.estabelecimento?.nome || '';

    if (navigator.share) {
      navigator.share({
        title: titulo,
        url,
      }).catch(() => {
        // Usuario cancelou ou erro - fallback para clipboard
        this.copiarParaClipboard(url);
      });
    } else {
      this.copiarParaClipboard(url);
    }
  }

  private copiarParaClipboard(url: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('cardapio_digital.loja_fechada.link_copiado'),
          life: 2000,
        });
      });
    }
  }

  private parseHorarios(): void {
    this.horarios = parseHorariosFuncionamento(this.estabelecimento?.horarioFuncionamento, this.translate);
  }

  /** Atualiza data e hora atual formatadas de acordo com o idioma */
  private atualizarDataHora(): void {
    const agora = new Date();
    const locale = this.translate.currentLang === 'es' ? 'es-PY' : 'pt-BR';
    this.dataAtual = agora.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    this.horaAtual = agora.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
}
